import React, { useState, useMemo, useEffect } from "react";
import { getRole } from "../../../helpers";
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  Pagination,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ReceiptIcon from "@mui/icons-material/Receipt";
import {
  AdminDashboardSummary,
  AdminDashboardProjectCount,
  AdminDashboardProductCompanyCount,
  AdminDashboardProductCountryCount,
  AdminDashboardCompanyCount,
  AdminDashboardCountryProduct,
  AdminDashboardProductCompanySales,
  AdminDashboardProductPeriodSales,
  AdminDashboardProductCountrySales,
  AdminDashboardCompanyProductSales,
  AdminDashboardCompanyPeriodSales,
  AdminDashboardCompanyCountrySales,
  AdminDashboardCountryProductSales,
  AdminDashboardCountryPeriodSales,
  AdminDashboardCountryCompanySales,
  AdminDashboardPeriodProductSales,
  AdminDashboardPeriodCountrySales,
  AdminDashboardPeriodCompanySales,
  AdminDashboardPackPeriodSales,
  AdminDashboardTreatmentPeriodSales,
} from "./dashboard-tables";

import "./admin-dashboard.css";

const ALL_TABS = [
  { id: "SUMMARY", label: "SUMMARY" },
  { id: "PRODUCT_PROJECT", label: "PRODUCT/PROJECT" },
  { id: "COUNTRY_FILINGS", label: "COUNTRY FILINGS" },
  { id: "SALES_BY_PRODUCT", label: "SALES BY PRODUCT" },
  { id: "SALES_BY_COMPANY", label: "SALES BY COMPANY" },
  { id: "SALES_BY_COUNTRY", label: "SALES BY COUNTRY" },
  { id: "SALES_OVER_TIME", label: "SALES OVER TIME" },
  { id: "PRICE", label: "PRICE" },
];

const AdminDashboard = (props) => {
  const [activeTab, setActiveTab] = useState("SUMMARY");
  const [statusFilter, setStatusFilter] = useState("All Data Submitted");

  const tabs = ALL_TABS;

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const renderContent = () => {
    switch (activeTab) {
      case "SUMMARY":
        return <AdminDashboardSummary history={props.history} />;
      case "PRODUCT_PROJECT":
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              width: "100%",
            }}
          >
            <AdminDashboardProjectCount />
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                width: "100%",
                "& > *": {
                  flex: "1 1 calc(50% - 1rem)",
                  minWidth: "300px",
                },
              }}
            >
              <AdminDashboardProductCompanyCount />
              <AdminDashboardCompanyCount />
            </Box>
          </Box>
        );
      case "COUNTRY_FILINGS":
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              width: "100%",
              "& > *": {
                flex: "1 1 calc(50% - 1rem)",
                minWidth: "300px",
              },
            }}
          >
            <AdminDashboardProductCountryCount />
            <AdminDashboardCountryProduct />
          </Box>
        );
      case "SALES_BY_PRODUCT":
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              width: "100%",
              "& > *": {
                flex: "1 1 calc(50% - 1rem)",
                minWidth: "300px",
                "&:nth-of-type(3)": {
                  flex: "1 1 100%",
                  marginTop: 2,
                },
              },
            }}
          >
            <AdminDashboardProductCompanySales />
            <AdminDashboardProductPeriodSales />
            <AdminDashboardProductCountrySales />
          </Box>
        );
      case "SALES_BY_COMPANY":
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              width: "100%",
              "& > *": {
                flex: "1 1 calc(50% - 1rem)",
                minWidth: "300px",
                "&:nth-of-type(3)": {
                  flex: "1 1 100%",
                  marginTop: 2,
                },
              },
            }}
          >
            <AdminDashboardCompanyProductSales />
            <AdminDashboardCompanyPeriodSales />
            <AdminDashboardCompanyCountrySales />
          </Box>
        );
      case "SALES_BY_COUNTRY":
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              width: "100%",
              "& > *": {
                flex: "1 1 calc(50% - 1rem)",
                minWidth: "300px",
                "&:nth-of-type(3)": {
                  flex: "1 1 100%",
                  marginTop: 2,
                },
              },
            }}
          >
            <AdminDashboardCountryProductSales />
            <AdminDashboardCountryPeriodSales />
            <AdminDashboardCountryCompanySales />
          </Box>
        );
      case "SALES_OVER_TIME":
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              width: "100%",
              "& > *": {
                flex: "1 1 calc(50% - 1rem)",
                minWidth: "300px",
                "&:nth-of-type(3)": {
                  flex: "1 1 100%",
                  marginTop: 2,
                },
              },
            }}
          >
            <AdminDashboardPeriodProductSales />
            <AdminDashboardPeriodCountrySales />
            <AdminDashboardPeriodCompanySales />
          </Box>
        );
      case "PRICE":
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              width: "100%",
              "& > *": {
                flex: "1 1 calc(50% - 1rem)",
                minWidth: "300px",
              },
            }}
          >
            <AdminDashboardPackPeriodSales />
            <AdminDashboardTreatmentPeriodSales />
          </Box>
        );
      default:
        return <AdminDashboardSummary history={props.history} />;
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "#F5F6FA",
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        p: 0,
      }}
    >
      <Typography variant="h5" component="h1" sx={{ mt: 2, mb: 2, fontSize: '1.75rem !important', fontWeight: 500 }}>
        Dashboard
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, overflowX: "auto" }}>
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            variant={activeTab === tab.id ? "contained" : "text"}
            disableElevation
            sx={{
              borderRadius: activeTab === tab.id ? "6px 6px 0 0" : 0,
              bgcolor: activeTab === tab.id ? "#28398A" : "transparent",
              color: activeTab === tab.id ? "#fff" : "#28398A",
              borderColor: "transparent",
              whiteSpace: "nowrap",
              px: 1.5,
              py: 1.5,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.4px",
              boxShadow: "none",
              minWidth: "100px",
              "&:hover": {
                bgcolor:
                  activeTab === tab.id ? "#1a237e" : "#fff",
                boxShadow: "none",
              },
            }}
          >
            {tab.label}
          </Button>
        ))}
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          width: "100%",
        }}
      >
        {renderContent()}
      </Box>
    </Box>
  );
};

export default AdminDashboard;
